import { API_BASE_URL, APP_POOL_CONFIG } from '@synergy/config';
import { ErrorIcon } from '@synergy/theme-next';
import { FormattedMessage } from 'react-intl';
import { Localized, PoolFormattedMessage } from '@synergy/components';
import {
    MarkdownConverter,
    PoolButton,
    PoolFormikCheckbox,
    PoolFormikPasswordField,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTypography,
} from '@synergy/components-next';
import { MouseEvent, ReactElement, useState } from 'react';
import { Stack, styled } from '@mui/material';
import { StaticSiteDto } from '@synergy/models';

import { ErrorText } from '../common/components/error-text/error-text';
import { LoginApi } from '../common/api/login-api';

const LabelText = styled('span')(({ theme }) => ({
    color: theme.palette.grey[700],
    '> span': {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            color: theme.palette.primary.dark,
            textDecoration: 'underline',
        },
    },
}));

export const ConfirmUserForm = ({ statusCode }: { statusCode: string | null }): ReactElement => {
    const [agbText, setAgbText] = useState<StaticSiteDto | null>(null);
    const api = new LoginApi(API_BASE_URL);

    const handleAgbClick = async (e: MouseEvent<HTMLSpanElement>): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();
        const response = await api.getAgb();
        if (response.success) {
            setAgbText(response.result);
        }
    };

    const handleAgbClose = () => setAgbText(null);

    return (
        <Stack
            typography="body1"
            justifyContent="flex-start"
            textAlign="center"
            alignSelf="flex-start"
            gap={2}
            sx={{ w: 7.5 }}
        >
            <PoolTypography component="div" variant="h4" translationId="app.confirmUser" />
            <PoolFormattedMessage
                id="app.confirmUser.subtitle"
                values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
            />
            <Stack spacing={2} sx={{ mt: 1.5, alignItems: 'center' }}>
                <PoolFormikPasswordField
                    name="password"
                    label={<FormattedMessage id="app.newPassword" />}
                    fullWidth={true}
                    required={true}
                    autoFocus={true}
                    sx={{ my: 0 }}
                    autoComplete="new-password"
                />
                <PoolFormikPasswordField
                    name="passwordConfirmation"
                    label={<FormattedMessage id="app.newPassword.passwordConfirm" />}
                    fullWidth={true}
                    required={true}
                    sx={{ mb: 0 }}
                    autoComplete="new-password"
                />
                <PoolFormikCheckbox
                    name="agb"
                    variant="grey"
                    label={
                        <LabelText>
                            <PoolFormattedMessage id="app.confirmUser.agb" values={{ onClick: handleAgbClick }} />
                        </LabelText>
                    }
                />
                {statusCode && (
                    <ErrorText sx={{ mt: 2 }} statusCode={statusCode}>
                        <ErrorIcon />
                    </ErrorText>
                )}
                <PoolButton variant="contained" type="submit" fullWidth={true} sx={{ mt: 4 }}>
                    <FormattedMessage id="app.newPassword.savePassword" />
                </PoolButton>
            </Stack>

            <PoolModal open={!!agbText} onClose={handleAgbClose} maxWidth="lg">
                <PoolModalTitle>
                    <Localized value={agbText?.title} />
                </PoolModalTitle>
                <PoolModalContent sx={{ textAlign: 'left' }}>
                    <Localized value={agbText?.text}>
                        {({ localization }) => <MarkdownConverter value={localization} />}
                    </Localized>
                </PoolModalContent>
                <PoolModalActions>
                    <PoolButton variant="outlined" onClick={handleAgbClose}>
                        <FormattedMessage id="app.common.close" />
                    </PoolButton>
                </PoolModalActions>
            </PoolModal>
        </Stack>
    );
};
